import React, { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useResourceAssignments } from "./useResourceInsightsData";
import { assignmentQuery } from "./filterModel";
import { pluralize } from "./cardFormat";
import AssignmentFilters from "./AssignmentFilters";
import TasksAccordion from "./TasksAccordion";
import styles from "./resourceInsights.module.scss";

/**
 * The tasks a resource is allocated to, filtered as the user chooses, loaded a page at a time and grouped by project,
 * scope and task group. Pressing a task moves the planner to its allocation; the pencil opens the task for editing.
 *
 * @param filters  what the list is filtered by, owned by Resource Insights so that a count of the overview can set it
 * @param defaults  the filters Resource Insights starts from: active tasks in its period
 * @param scopeProjectId  the project the whole card is limited to, when it is
 * @param projects  the projects of the resource, for the project filter
 */
export default function AssignmentsTab({ resourceId, scopeProjectId, projects, filters, defaults, onFiltersChange, showHours, onNavigate, onEdit }) {
  const query = useMemo(() => assignmentQuery(filters, scopeProjectId), [filters, scopeProjectId]);
  const { items, totalCount, hasMore, isLoading, isLoadingMore, error, loadMore, reload } = useResourceAssignments({ resourceId, query });

  return (
    <div className={styles.tabContent}>
      <AssignmentFilters filters={filters} defaults={defaults} projects={projects} onChange={onFiltersChange} />

      {!isLoading && !error && <p className={styles.resultCount}>{pluralize(totalCount, "task")}</p>}

      {isLoading && (
        <div className={styles.cardList}>
          {[0, 1, 2].map((placeholder) => (
            <Skeleton key={placeholder} height={92} borderRadius={10} />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <div className={styles.errorBox}>
          <span>The assignments could not be loaded.</span>
          <button type="button" className={styles.linkButton} onClick={reload}>
            Try again
          </button>
        </div>
      )}

      {!isLoading && !error && items.length === 0 && <p className={styles.empty}>No tasks match these filters.</p>}

      {!isLoading && items.length > 0 && <TasksAccordion tasks={items} showHours={showHours} onNavigate={onNavigate} onEdit={onEdit} />}

      {isLoadingMore && <Skeleton height={92} borderRadius={10} />}

      {hasMore && !isLoadingMore && (
        <button type="button" className={styles.loadMore} onClick={loadMore}>
          Load more
        </button>
      )}
    </div>
  );
}
